import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import SEO from "@/components/SEO";
import SafeImage from "@/components/SafeImage";
import { Reveal, Kicker } from "@/components/Reveal";
import { api } from "@/lib/api";

const FILTERS = ["All", "Store", "Products", "Electrical", "Automation", "Switchgear", "Pneumatic", "Team"];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const reduce = useReducedMotion();

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const visible = filter === "All" ? items : items.filter((g) => g.category === filter);

  return (
    <>
      <SEO
        title="Gallery | Hari Om Trading Co. — Tahliwal, Una"
        description="Gallery of store, product, electrical, automation, switchgear and pneumatic imagery from Hari Om Trading Co., Tahliwal."
      />
      <section className="grain bg-[#111111] bg-grid-dark py-16 lg:py-24" data-testid="gallery-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Kicker dark>Gallery</Kicker>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Store &amp; Product Gallery
            </h1>
            <p className="mt-4 max-w-2xl text-base text-[#b8bec8]">
              Demo images shown use licensed stock photography and will be replaced with authentic
              business photographs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F6F7F8] py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" role="tablist" aria-label="Gallery filters" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                className={`min-h-[44px] shrink-0 border px-5 py-2.5 text-sm font-semibold transition-colors ${
                  filter === f
                    ? "border-[#111111] bg-[#111111] text-[#F5B700]"
                    : "border-[#D9DDE3] bg-white text-[#161616] hover:border-[#111111]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-8 grid animate-pulse gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-[#e5e8ec]" />)}
            </div>
          ) : (
            <motion.div layout className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
              <AnimatePresence mode="popLayout">
                {visible.map((g) => (
                  <motion.figure
                    key={g.id}
                    layout
                    initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.35 }}
                    className="group border border-[#D9DDE3] bg-white"
                    data-testid={`gallery-item-${g.category.toLowerCase()}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#202124]">
                      <SafeImage
                        src={g.image}
                        alt={g.caption}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                      />
                    </div>
                    <figcaption className="p-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8a6800]">{g.category}</p>
                      <p className="mt-1 text-xs leading-relaxed text-[#667085]">{g.caption}</p>
                    </figcaption>
                  </motion.figure>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
